import * as dateFns from 'date-fns';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';

interface SetDateTimeParams {
  date: Date;
  daysToAdd?: number;
  hours?: number;
  minutes?: number;
}

export const FORM_NAMES = {
  address2: 'address2',
  content: 'content',
  date: 'date',
  dateFormGroup: 'dateFormGroup',
  description: 'description',
  emailInput: 'emailInput',
  end: 'end',
  eventLocation: 'event_location',
  featuredAccountInput: 'featuredAccountInput',
  featuredAccounts: 'featured_accounts.0',
  featuredAccountsFormGroup: 'featuredAccountsFormGroup',
  group: 'group',
  locationFormGroup: 'locationFormGroup',
  locationInput: 'locationInput',
  meetingMethod: 'meeting_method',
  notificationRecipientsCustomSegmentNodes:
    'notification_recipients.custom_segment_nodes',
  notificationRecipientsEmails: 'notification_recipients.emails',
  notificationRecipientsHost: 'notification_recipients.host',
  notificationRecipientsRoles: 'notification_recipients.roles',
  notificationRecipientsRsvps: 'notification_recipients.rsvps',
  notificationRecipientsUseExclusiveMatching:
    'notification_recipients.use_exclusive_matching',
  opportunityImage: 'opportunity_image',
  opportunityType: 'opportunity_type',
  repeatEvent: 'repeat_event',
  rsvpLimit: 'rsvp_limit',
  start: 'start',
  submissionInstructions: 'submission_instructions.0',
  title: 'title',
};

const EVENT_KEYS = [
  '_id',
  'content',
  'description',
  'encoded_radius_path',
  'end_date',
  'event_end_date',
  'event_start_date',
  'event_location',
  'featured_accounts',
  'meeting_method',
  'notification_recipients',
  'online_location',
  'opportunity_image',
  'opportunity_type',
  'owner_group',
  'repeat_event',
  'restricted_group',
  'rsvp_end_date',
  'rsvp_limit',
  'rsvp_list_confirmed',
  'start_date',
  'status',
  'submission_instructions',
  'title',
  'use_approximate_address',
];
const DEFAULT_EVENT_PROPERTIES = {
  rsvp_list_confirmed: false,
  use_approximate_address: true,
};
const SLUG_LIMIT = 50;

export const OPPORTUNITY_EVENT_TYPES = {
  event: 'First Come, First Served',
  exclusive_event: 'Select from Registration List',
};
export const DEFAULT_DATE = {
  days: 14,
  startHour: 17,
  endHour: 18,
};
export const EVENT_STATUS_ACTIVE = { status: 'active' };
export const EVENT_STATUS_DRAFT = { status: 'draft' };
export const EVENT_STATUS_REQUESTED = { status: 'requested' };
export const CONFIRM_REQUEST_DIALOG = {
  title: 'Request this Office Hour Event',
  content: 'Your Office Hour Event will be pending until approved by an Admin.',
  cancel: 'Back to Editor',
  confirm: 'Request',
};
export const FORM_ERROR_MESSAGES = {
  [FORM_NAMES.dateFormGroup]: 'The dates you have chosen are invalid.',
  [FORM_NAMES.description]: 'Please add a description.',
  [FORM_NAMES.emailInput]: 'Please add emails to notify.',
  [FORM_NAMES.featuredAccountsFormGroup]: 'Please select a host.',
  [FORM_NAMES.locationFormGroup]: 'Please fill out the joining info.',
  [FORM_NAMES.title]: 'Please add a title.',
};

export const TOAST_MESSAGE_DRAFT = 'Saved';
export const TOAST_MESSAGE_PUBLISHED = 'Event Posted!';
export const TOAST_MESSAGE_UPDATED = 'Event Updated!';
export const TOAST_MESSAGE_REPEAT_PUBLISHED = 'Events Posted!';
export const TOAST_MESSAGE_REQUESTED = 'Sent for approval';
export const TOAST_MESSAGE_REQUEST_UPDATED = 'Request Updated';

export const EVENT_UNSAVED_CHANGES_DIALOG = {
  title: 'Edit in progress',
  content: `You're still editing this Office Hour, if you leave now your changes won't be saved.`,
  cancel: 'Keep Editing',
  confirm: 'Discard Changes',
};

export function sanitizeFormValuesForPost(
  formGroupValue: any,
  statusConfig: { status: string },
): any {
  const dates = calculateEventDatesFromFormGroup(formGroupValue.dateFormGroup);
  const sanitizedLocation = sanitizeLocation(formGroupValue);
  return pick(
    {
      ...unflattenForm({
        ...formGroupValue,
      }),
      ...dates,
      ...statusConfig,
      ...sanitizedLocation,
      owner_group: formGroupValue.group,
      restricted_group: formGroupValue.group,
      ...DEFAULT_EVENT_PROPERTIES,
    },
    EVENT_KEYS,
  );
}

export function calculateEventDatesFromFormGroup({
  date,
  start,
  end,
  opportunity_type,
}: {
  date: Date;
  start: Date;
  end: Date;
  opportunity_type: string;
}): any {
  const timeForm: any = {};
  if (start) {
    const startDate = new Date(start);
    const now = new Date();
    const validStartTime = dateFns.isBefore(now, startDate) ? startDate : now;
    const eventStartDate = copyDateObject({
      date: new Date(date),
      time: new Date(validStartTime),
    });
    const startDateCopy = new Date(eventStartDate);
    const rsvpEndDate = calculateRsvpEndDate(startDateCopy, opportunity_type);
    timeForm.start_date = now;
    timeForm.event_start_date = eventStartDate;
    timeForm.rsvp_end_date = rsvpEndDate;
  }
  if (end) {
    const eventEndDate = copyDateObject({
      date: new Date(date),
      time: new Date(end),
    });
    timeForm.event_end_date = eventEndDate;
    timeForm.end_date = eventEndDate;
  }
  return timeForm;
}

export function calculateRsvpEndDate(
  startDate: Date,
  opportunityType: string,
): Date {
  const isExclusiveEvent = isEqual('exclusive_event', opportunityType);
  if (isExclusiveEvent) {
    startDate.setHours(startDate.getHours() - 1);
  }
  return startDate;
}

export function sanitizeLocation(formGroupValue: any): any {
  const { locationInput, event_location, meeting_method, address2 } =
    formGroupValue.locationFormGroup;
  if (!isEqual('in-person', meeting_method)) {
    return {
      event_location: {},
      online_location: locationInput,
      meeting_method,
    };
  }
  if (
    isEqual(
      locationInput,
      get(event_location, 'formatted_address', event_location.address),
    )
  ) {
    return {
      meeting_method,
      online_location: '',
      event_location: {
        address: event_location.address,
        address2,
        custom_label: event_location.custom_label,
        location: event_location.location,
        name: event_location.name,
      },
    };
  }
  return { event_location: {}, online_location: '', meeting_method };
}

export function copyDateObject({
  date,
  time,
}: {
  date: Date;
  time: Date;
}): Date {
  return setDateTime({
    date,
    hours: time.getHours(),
    minutes: time.getMinutes(),
  });
}

function unflattenForm(form: any): any {
  return reduce(form, (result, value, key) => set(result, key, value), {});
}

export function setDateTime({
  date,
  daysToAdd = 0,
  hours = 0,
  minutes = 0,
}: SetDateTimeParams): Date {
  date.setDate(date.getDate() + daysToAdd);
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
}

export function generateSlugWithDate(title: string, startDate: Date): string {
  const slugCandidate = title.toLowerCase().split(' ').join('-');
  const date = new Date(startDate);
  const monthString =
    10 > date.getMonth() + 1 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const dayString = 10 > date.getDate() ? `0${date.getDate()}` : date.getDate();
  const dateString = `${date.getFullYear()}${monthString}${dayString}`;
  return `${dateString}-${slugCandidate}`.slice(0, SLUG_LIMIT);
}
